import proj4 from 'proj4';
import type { FeatureCollection, Geometry } from 'geojson';
import type { LoadedData, LookupRecord, PublicationManifest } from './types';

// proj4js does not ship every EPSG definition. Register the approved canonical
// CRS explicitly so a production browser cannot fail during map-click matching.
proj4.defs('EPSG:26910', '+proj=utm +zone=10 +datum=NAD83 +units=m +no_defs');

export const PREDICTOR_LABELS: Record<string, string> = {
  elevation_m: 'Elevation',
  slope_deg: 'Slope',
  aspect_sin: 'Aspect (sin)',
  aspect_cos: 'Aspect (cos)',
  curvature: 'Curvature',
  distance_to_hydrography_m: 'Distance to Flowline',
  land_cover_class: 'NLCD class',
  annual_precipitation_mm: 'Annual precipitation',
};

export const CLASS_LABELS: Record<number, string> = {
  0: 'Unavailable',
  1: '<20th percentile',
  2: '20th–<40th percentile',
  3: '40th–<60th percentile',
  4: '60th–<80th percentile',
  5: '≥80th percentile',
};

export type LookupResult =
  | { status: 'matched'; clicked: { longitude: number; latitude: number }; record: LookupRecord; distance_m: number; maximum_m: number }
  | { status: 'unavailable'; clicked: { longitude: number; latitude: number }; nearest_distance_m?: number; maximum_m: number; reason: string };

const DISPLAY_BOUNDS_MARGIN = { longitude: 0.12, latitude: 0.10 };

export function publicationBase(): string {
  return import.meta.env.VITE_PUBLICATION_BASE || `${import.meta.env.BASE_URL}assets`;
}

export function assetUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

async function responseJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Asset request failed (${response.status}): ${url}`);
  return response.json() as Promise<T>;
}

async function decompressedLines(url: string): Promise<string[]> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Compressed asset request failed (${response.status}): ${url}`);
  const contentEncoding = response.headers.get('content-encoding')?.toLowerCase() ?? '';
  const stream = contentEncoding.includes('gzip')
    ? response.body
    : (response.body && 'DecompressionStream' in globalThis ? response.body.pipeThrough(new DecompressionStream('gzip')) : null);
  if (!stream && !contentEncoding.includes('gzip')) throw new Error('This browser cannot decompress the published gzip lookup assets.');
  if (!stream) throw new Error(`Compressed asset has no readable body: ${url}`);
  const text = await new Response(stream).text();
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  if (!lines.length) throw new Error(`Compressed asset is empty: ${url}`);
  return lines;
}

async function responseGzipRecord<T>(url: string): Promise<T> {
  const lines = await decompressedLines(url);
  return JSON.parse(lines[0]) as T;
}

async function responseGzipNdjson<T>(url: string): Promise<T[]> {
  return (await decompressedLines(url)).map((line) => JSON.parse(line) as T);
}

export function matchLookup(records: LookupRecord[], longitude: number, latitude: number, maximum_m: number): LookupResult {
  const clicked = { longitude, latitude };
  if (!records.length || maximum_m < 0) return { status: 'unavailable', clicked, maximum_m, reason: 'no_valid_lookup_within_maximum_distance' };
  let clickedX: number;
  let clickedY: number;
  try {
    [clickedX, clickedY] = proj4('EPSG:4326', 'EPSG:26910', [longitude, latitude]);
  } catch {
    return { status: 'unavailable', clicked, maximum_m, reason: 'coordinate_transform_failed' };
  }
  let nearest = records[0];
  let nearestSquared = Number.POSITIVE_INFINITY;
  for (const record of records) {
    const dx = record.matched_location.x_m - clickedX;
    const dy = record.matched_location.y_m - clickedY;
    const squared = dx * dx + dy * dy;
    if (squared < nearestSquared || (squared === nearestSquared && record.lookup_id < nearest.lookup_id)) {
      nearest = record;
      nearestSquared = squared;
    }
  }
  const distance_m = Math.sqrt(nearestSquared);
  if (distance_m > maximum_m) return { status: 'unavailable', clicked, nearest_distance_m: distance_m, maximum_m, reason: 'no_valid_lookup_within_maximum_distance' };
  return { status: 'matched', clicked, record: nearest, distance_m, maximum_m };
}

function coordinatePairs(value: unknown): [number, number][] {
  if (!Array.isArray(value)) return [];
  if (value.length >= 2 && typeof value[0] === 'number' && typeof value[1] === 'number') return [[value[0], value[1]]];
  return value.flatMap((item) => coordinatePairs(item));
}

export function validateObservationCollection(
  value: unknown,
  sourceBounds: [number, number, number, number],
): asserts value is FeatureCollection<Geometry, Record<string, unknown>> {
  if (!value || typeof value !== 'object' || (value as { type?: unknown }).type !== 'FeatureCollection') {
    throw new Error('The observation asset is not a GeoJSON FeatureCollection.');
  }
  const features = (value as { features?: unknown }).features;
  if (!Array.isArray(features)) throw new Error('The observation asset has no GeoJSON feature list.');
  const bounds = [
    sourceBounds[0] - DISPLAY_BOUNDS_MARGIN.longitude,
    sourceBounds[1] - DISPLAY_BOUNDS_MARGIN.latitude,
    sourceBounds[2] + DISPLAY_BOUNDS_MARGIN.longitude,
    sourceBounds[3] + DISPLAY_BOUNDS_MARGIN.latitude,
  ];
  for (const [index, feature] of features.entries()) {
    const geometry = (feature as { geometry?: { type?: unknown; coordinates?: unknown } } | null)?.geometry;
    if (!geometry || !['Polygon', 'MultiPolygon'].includes(String(geometry.type))) {
      throw new Error(`The observation asset has unsupported geometry at feature ${index}.`);
    }
    const coordinates = coordinatePairs(geometry.coordinates);
    if (!coordinates.length || coordinates.some(([longitude, latitude]) => !Number.isFinite(longitude) || !Number.isFinite(latitude) || longitude < bounds[0] || longitude > bounds[2] || latitude < bounds[1] || latitude > bounds[3])) {
      throw new Error(`The observation asset has invalid or out-of-display-bounds coordinates at feature ${index}.`);
    }
  }
}

export async function loadPublication(base = publicationBase()): Promise<LoadedData> {
  const manifest = await responseJson<PublicationManifest>(assetUrl(base, 'publication_manifest.json'));
  if (manifest.status !== 'complete' || !manifest.web_map?.score_tile_template || !manifest.web_map.class_tile_template || !manifest.web_map.terrain?.tile_template || manifest.web_map.terrain.encoding !== 'terrarium') {
    throw new Error('The publication manifest is incomplete or lacks browser raster tiles.');
  }
  const [lookup, observations, folds, global, metrics, methodology] = await Promise.all([
    responseGzipNdjson<LookupRecord>(assetUrl(base, manifest.assets['location_lookup.ndjson.gz'].path)),
    responseGzipRecord<FeatureCollection<Geometry, Record<string, unknown>>>(assetUrl(base, manifest.assets['landslide_observations.geojson.ndjson.gz'].path)),
    responseGzipRecord<FeatureCollection<Geometry, Record<string, unknown>>>(assetUrl(base, manifest.assets['validation_folds.geojson.ndjson.gz'].path)),
    responseJson<LoadedData['global']>(assetUrl(base, manifest.assets['global_explainability.json'].path)),
    responseJson<LoadedData['metrics']>(assetUrl(base, manifest.assets['metrics_summary.json'].path)),
    responseJson<LoadedData['methodology']>(assetUrl(base, manifest.assets['methodology.json'].path)),
  ]);
  if (!Array.isArray(lookup) || lookup.some((record) => record.relative_susceptibility_class < 1 || record.relative_susceptibility_class > 5)) {
    throw new Error('The lookup asset failed its class-domain check.');
  }
  validateObservationCollection(observations, manifest.canonical_grid.source_bbox_wgs84);
  return { manifest, lookup, observations, folds, global, metrics, methodology };
}
